'use client'

// The shrine on the main page. Opens as a full-width drawer that slides
// up from the bottom and stops short of the top bar, so the world (and
// the clock, and him) stay in view while you write. Replaced the centered
// modal: Sam's read was that modals fight the cute of the rest of the UI,
// and the old layout hid the feed behind a side pull. Everything is on
// one surface now:
//   • composer (signed-in only) — leave an offering without leaving the world
//   • live feed — offerings + {BIRD_NAME}'s verdicts, newest first
//   • "lately he keeps circling: …" — his current, MOVING taste
//   • your standing (signed in) — the earned relationship, at a glance
//
// Data: polls /api/sacrifices + /api/role + /api/interests, AND subscribes
// to the WS event stream (gameStore.events) — a JUDGE event refreshes
// instantly and, when the modal is closed, badges the button with a gold
// dot so you know {BIRD_NAME} just read something. /shrine stays the deep view.

import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { agoShort } from '../../lib/ui/time'
import { SACRIFICE_TEXT_LIMIT as TEXT_LIMIT } from '../../lib/world/sacrificeTiers'
import { useAccount, authFetch } from '../auth/useUser'
import useGameStore from '../../stores/gameStore'
import { resolveTier, submitsPerDay, CALENDAR_POWERS } from '../../lib/tiers'
import { notesLine } from '../../lib/notesWords'
import { shareIntent, shareThreadIntent } from '../../lib/ui/shareIntent'
import { sortedWaiting, expiredWords } from '../../lib/ui/sorterWords'
import { Card, XShareButton } from './Drawer'
import { Tabs } from './Tabs'

const POSTER_MIN = CALENDAR_POWERS.find((p) => p.id === 'propose').min
import { KawaiiButton, GiftIcon, CloseButton, kawaiiPanel } from './KawaiiButton'
import { talkLockReason } from '../../lib/community'
import { ui, kawaii } from '../../lib/ui/theme'
import { KawaiiCta } from './KawaiiButton'
import { BIRD_NAME } from '../../lib/world/identity'


const POLL_MS = 12_000
// The feed is the town noticeboard now: on the busiest day so far he was
// sent 27 things, and a one-card carousel showing 12 of 188 meant a
// visitor who came to see what people say to him saw the last hour. Sixty
// in the first read, sixty more per press, capped where the API caps.
const FEED_LIMIT = 60
const FEED_MAX = 200


export function OfferingsDock() {
  // Whose verdict just landed vs who is looking: the ownership check
  // in the JUDGE effect below needs the viewer's own handle.
  const { username, called } = useAccount()
  // Opened from the corner menu; the modal still closes itself.
  const open = useGameStore((st) => st.shrineOpen)
  const setOpen = useGameStore((st) => st.setShrineOpen)
  const [sacrifices, setSacrifices] = useState([])
  // how many wait for him and how many he has read today, for the line under the board (the board is what he has read)
  const [counts, setCounts] = useState(null)
  const [feedLimit, setFeedLimit] = useState(FEED_LIMIT)
  const [circling, setCircling] = useState(null)
  const [role, setRole] = useState(null)
  const setShrineUnseen = useGameStore((st) => st.setShrineUnseen)
  const markSeen = useGameStore((st) => st.markSeen)
  const setShrineTalk = useGameStore((st) => st.setShrineTalk)
  // /?talk=<id> is the door the thread page's "join in" opens: the
  // drawer, on the talk tab, with that thread first and unfolded.
  useEffect(() => {
    try {
      const id = new URLSearchParams(window.location.search).get('talk')
      if (id && /^[0-9a-f-]{36}$/i.test(id)) { setShrineTalk(id); setOpen(true) }
    } catch { /* no window, no door */ }
  }, [setShrineTalk, setOpen])
  const lastJudgedAt = useGameStore((st) => st.noticeState.lastJudgedAt)
  const openRef = useRef(open)
  useEffect(() => { openRef.current = open }, [open])

  const refreshFeed = useCallback(async () => {
    try {
      const res = await fetch(`/api/sacrifices?limit=${feedLimit}`, { cache: 'no-store' })
      const body = await res.json().catch(() => ({}))
      if (Array.isArray(body.sacrifices)) setSacrifices(body.sacrifices)
      if (typeof body.waiting === 'number') setCounts({ waiting: body.waiting, readToday: body.readToday || 0 })
    } catch { /* keep stale */ }
  }, [feedLimit])
  const showMore = useCallback(() => setFeedLimit((n) => Math.min(FEED_MAX, n + FEED_LIMIT)), [])

  const setMyOfferingIds = useGameStore((st) => st.setMyOfferingIds)
  const refreshMeta = useCallback(async () => {
    // Which of the offerings out there are YOURS, for the map markers.
    // Signed out this returns an empty list and costs nothing.
    try {
      const res = await authFetch('/api/sacrifices?mine=1&limit=20', { cache: 'no-store' })
      const body = await res.json().catch(() => ({}))
      if (Array.isArray(body.sacrifices)) setMyOfferingIds(body.sacrifices.map((o) => o.id))
    } catch { /* keep stale */ }
    try {
      const res = await fetch('/api/interests', { cache: 'no-store' })
      const body = await res.json().catch(() => null)
      setCircling(body?.interests || null)
    } catch { /* keep stale */ }
    try {
      const res = await authFetch('/api/role', { cache: 'no-store' })
      const body = await res.json().catch(() => null)
      if (body?.tier) {
        setRole(body)
        // The menu asks whether to offer the poster shelf, and this is the
        // one role read that happens on page load rather than on a drawer
        // being opened, so it is the honest place to answer from.
        useGameStore.getState().setCanPoster((body.accepted || 0) >= POSTER_MIN)
      }
    } catch { /* keep stale */ }
  }, [setMyOfferingIds])

  // Polls only while the dock is actually open. Ungated, every tab was
  // paying 300 Neon queries an hour into a closed drawer, plus the
  // three-fetch meta round; the audit found this was the only drawer not
  // gated on open. What genuinely must work while shut is covered
  // without polling: the WS JUDGE handler below badges the button and
  // refreshes, and one meta fetch on mount seeds the minimap ids.
  useEffect(() => {
    refreshMeta()
  }, [refreshMeta])

  useEffect(() => {
    if (!open) return undefined
    refreshFeed()
    refreshMeta()
    const a = setInterval(refreshFeed, POLL_MS)
    const b = setInterval(refreshMeta, POLL_MS * 5)
    return () => { clearInterval(a); clearInterval(b) }
  }, [open, refreshFeed, refreshMeta])

  // Instant verdicts: a JUDGE event on the WS stream refreshes the feed
  // immediately; when the modal is closed it badges the button instead.
  const events = useGameStore((s) => s.events)
  const lastJudgeIdRef = useRef(null)
  useEffect(() => {
    // Newest JUDGE event: the buffer appends newest LAST, so find() locked
    // onto the first-ever verdict and later ones never badged or refreshed.
    const judge = (events || []).findLast((e) => e.action === 'JUDGE')
    if (!judge || judge.id === lastJudgeIdRef.current) return
    lastJudgeIdRef.current = judge.id
    refreshFeed()
    refreshMeta()
    // The dot is personal, the feed is not: any verdict refreshes the
    // list, but only a verdict on YOUR offering lights the corner. The
    // event's target is the offerer's handle, so an unbadged stranger's
    // verdict was pinging everyone signed in.
    // Either of your names counts: the target is whatever name rode the
    // offering, which is the chosen one once settings has it.
    const t = String(judge.target || '').toLowerCase()
    const yours = [username, called].filter(Boolean).some((n) => t === String(n).toLowerCase())
    // setShrineUnseen drives the badge; a local mirror of it was written
    // here for months and read by nothing.
    if (yours && !openRef.current) setShrineUnseen?.(true)
  }, [events, refreshFeed, refreshMeta, username, called])

  // Open is looking: whatever he judges while you have this in front of
  // you is already seen, so it must not light the corner behind the panel.
  useEffect(() => {
    if (open) markSeen('offerings')
  }, [open, lastJudgedAt, markSeen])

  // Escape closes it, same as every other drawer.
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <>
      {open && (
        <ShrineDrawer
          onClose={() => setOpen(false)}
          sacrifices={sacrifices}
          counts={counts}
          role={role}
          onSubmitted={() => { refreshFeed(); refreshMeta() }}
          onMore={feedLimit < FEED_MAX ? showMore : null}
        />
      )}
    </>
  )
}

// The drawer. Three things a visitor needs to get in one glance, side by
// side and labelled in plain words: how he sees you, leave him something,
// what he has been left. The portrait card stays (it is the best art in
// the product) but it is framed as his memory of you now rather than a
// prayer card, which fits where the language has moved.
// (22 Oct: "Let go" is what he did not keep, off the noticeboard since the feed became his conversation with people
// but still a thing people want to see; "Yours" is everything you left him, with the ones that went up on the wall
// and came down again, which somebody went looking for and could not find)
const SHRINE_TABS = [
  { id: 'offerings', label: 'Offerings' },
  { id: 'talk', label: 'Talk' },
  { id: 'gone', label: 'Let go' },
  { id: 'yours', label: 'Yours' },
]
const TITLES = { talk: (n) => `Talk with ${n}`, gone: (n) => `What ${n} let go`, yours: (n) => `What you left ${n}` }
// where your own offering stands, for the chips on the Yours tab
const YOURS_CHIPS = [
  ['all', 'all'],
  ['wall', 'on the wall'],
  ['down', 'came down'],
  ['gone', 'let go'],
  ['waiting', 'waiting on him'],
  ['glint', 'Glint read it'],
  ['expired', 'expired'],
]
function whereItStands(s) {
  const phase = s.status || 'pending'
  if (phase === 'accepted') return s.wallLeftAt ? 'down' : 'wall'
  if (phase === 'released') return 'down'
  if (phase === 'rejected') return 'gone'
  if (phase === 'expired') return 'expired'
  if (phase === 'glint') return 'glint'
  return 'waiting'
}

function ShrineDrawer({ onClose, sacrifices, counts, role, onSubmitted, onMore }) {
  const { signedIn } = useAccount()
  // Null until he picks one, so the opening can be decided by whoever
  // opened the drawer. Derived below, never set from an effect.
  const [picked, setPicked] = useState(null)
  const landOn = useGameStore((st) => st.shrineTalk)
  const tab = picked ?? (landOn ? 'talk' : 'offerings')
  const setTab = setPicked
  // The two new tabs fetch when opened, not with the feed: what he let go
  // and what is yours are read on purpose, not polled into a closed drawer.
  const [gone, setGone] = useState(null)
  const [mine, setMine] = useState(null)
  const [chip, setChip] = useState('all')
  useEffect(() => {
    if (tab !== 'gone') return
    let dead = false
    fetch('/api/sacrifices?gone=1&limit=100', { cache: 'no-store' })
      .then((r) => r.json()).then((b) => { if (!dead && Array.isArray(b.sacrifices)) setGone(b.sacrifices) })
      .catch(() => { if (!dead) setGone([]) })
    return () => { dead = true }
  }, [tab])
  useEffect(() => {
    if (tab !== 'yours' || !signedIn) return
    let dead = false
    authFetch('/api/sacrifices?mine=1&limit=200', { cache: 'no-store' })
      .then((r) => r.json()).then((b) => { if (!dead && Array.isArray(b.sacrifices)) setMine(b.sacrifices) })
      .catch(() => { if (!dead) setMine([]) })
    return () => { dead = true }
  }, [tab, signedIn])
  const yours = useMemo(() => (mine || []).filter((s) => chip === 'all' || whereItStands(s) === chip), [mine, chip])
  const setProfileOpen = useGameStore((st) => st.setProfileOpen)
  const setConnectThen = useGameStore((st) => st.setConnectThen)
  // One door for the whole site: the connect card lives in the profile
  // drawer, so a signed-out CTA closes this one and opens that, and asks
  // to be brought back here once connecting is done.
  const connect = () => { setConnectThen('shrine'); onClose(); setProfileOpen(true) }
  const visible = useMemo(() => sacrifices.slice(0, FEED_MAX), [sacrifices])
  // Still needed: the daily allowance is per rank, so the form has to know
  // which one you are. The distance to the NEXT rank went with the standing
  // column, which is the profile drawer's job now.
  const accepted = role?.accepted ?? 0
  const rank = role ? resolveTier(accepted) : null

  // Slide up on mount. One frame closed, then open, so the transition runs.
  const [entered, setEntered] = useState(false)
  useEffect(() => {
    const t = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(t)
  }, [])


  return (
    <div className="fixed inset-0 z-50 pointer-events-auto">
      {/* A light scrim, not a blackout: the world above the drawer is the
          point of keeping it visible. Click closes. */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(26,16,10,0.28)', opacity: entered ? 1 : 0, transition: 'opacity 300ms ease' }}
        onClick={onClose}
      />

      <div
        className="absolute inset-x-0 bottom-0 flex flex-col"
        style={{
          height: 'min(76dvh, 760px)',
          background: kawaii.cream,
          borderTop: kawaii.outline,
          borderRadius: '22px 22px 0 0',
          boxShadow: '0 -12px 40px rgba(26,16,10,0.35)',
          fontFamily: ui.font,
          transform: entered ? 'translateY(0)' : 'translateY(102%)',
          transition: 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <div style={{ position: 'absolute', inset: 6, bottom: 0, border: kawaii.stitch, borderBottom: 'none', borderRadius: '18px 18px 0 0', pointerEvents: 'none' }} />

        {/* Handle + header */}
        <div className="shrink-0 pt-2.5 pb-3 px-6 sm:px-10">
          <div className="mx-auto w-10 h-1.5 rounded-full mb-3" style={{ background: 'rgba(47,38,80,0.22)' }} />
          {/* Same max-width and centring as the grid below. */}
          <div className="mx-auto w-full max-w-5xl flex items-start justify-between gap-4">
            <div>
              {/* No strapline: the card under the form says all of this at
                  length now, and saying it twice made the header a wall. */}
              <span className="flex items-center gap-2 text-[19px] font-bold text-[#2f2650]">
                {TITLES[tab] ? TITLES[tab](BIRD_NAME) : `Leave ${BIRD_NAME} something`}
              </span>
            </div>
            <button onClick={onClose} aria-label="close" className="text-[15px] text-[#2f2650]/60 hover:text-[#2f2650] transition-colors px-2 py-1">✕</button>
          </div>
          <div className="mx-auto w-full max-w-5xl" style={{ marginTop: 10 }}>
            <Tabs tabs={SHRINE_TABS} active={tab} onPick={setTab} />
          </div>
        </div>

        {/* Body — one scroll surface, three labelled columns on desktop,
            a single sensible order on a phone: write first, then you,
            then everyone else. */}
        {/* The offerings side pins its own columns on desktop, so the
            scroll behaviour differs per tab rather than being one
            compromise that suits neither. */}
        <div className={`kawaii-scroll flex-1 min-h-0 px-6 sm:px-10 pb-14 md:pb-8 pt-1 overflow-y-auto${tab === 'offerings' ? ' md:overflow-hidden' : ''}`}>
          {tab === 'talk' && (
            <div className="max-w-5xl mx-auto">
              <TalkList standing={accepted} landOn={landOn} />
            </div>
          )}
          {tab === 'gone' && (
            <div className="max-w-5xl mx-auto">
              <p style={{ color: kawaii.inkSoft, fontSize: 13.5, fontWeight: 600, lineHeight: 1.55, marginBottom: 12 }}>
                What he did not keep, and what he kept a while up in the ruins and then took down. His reasons are his own.
              </p>
              {gone === null
                ? <p className="text-[13.5px] font-semibold text-[#2f2650]/60 italic py-2">looking…</p>
                : <FeedList items={gone} standing={accepted} empty={<p className="text-[13.5px] font-semibold text-[#2f2650]/60 italic py-2">he has let nothing go yet.</p>} />}
            </div>
          )}
          {tab === 'yours' && (
            <div className="max-w-5xl mx-auto">
              {!signedIn && (
                <Card className="text-center leading-relaxed" style={{ color: kawaii.inkSoft, fontSize: 14.5, fontWeight: 600, padding: '22px 18px' }}>
                  Connect to see everything you have left him, including what went up on the wall and came down again.
                  <div style={{ marginTop: 14 }}>
                    <KawaiiCta intent="primary" small onClick={connect}>connect to {BIRD_NAME}</KawaiiCta>
                  </div>
                </Card>
              )}
              {signedIn && (
                <>
                  <div className="flex items-center gap-1 flex-wrap" style={{ marginBottom: 12 }}>
                    {YOURS_CHIPS.map(([id, label]) => {
                      const on = chip === id
                      const n = id === 'all' ? (mine || []).length : (mine || []).filter((s) => whereItStands(s) === id).length
                      // (expired shows only when something of theirs has: most people's never will)
                      if ((id === 'expired' || id === 'glint') && !n && !on) return null
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setChip(id)}
                          style={{ padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 800, cursor: 'pointer', background: on ? kawaii.ink : kawaii.creamDeep, color: on ? kawaii.cream : kawaii.ink, border: kawaii.outlineThin }}
                        >
                          {label}{mine ? ` ${n}` : ''}
                        </button>
                      )
                    })}
                  </div>
                  {mine === null
                    ? <p className="text-[13.5px] font-semibold text-[#2f2650]/60 italic py-2">looking…</p>
                    : <FeedList items={yours} standing={accepted} empty={<p className="text-[13.5px] font-semibold text-[#2f2650]/60 italic py-2">{chip === 'all' ? 'you have not left him anything yet.' : 'nothing of yours stands there.'}</p>} />}
                </>
              )}
            </div>
          )}
          {/* gap-x matches the wallet drawer. The row gap stays generous
              because that is what separates the three sections when they
              stack on a phone. */}
          {/* Two halves. The standing column that used to sit on the left is
              gone: it is the whole of the profile drawer now, and repeating
              a rank pill here made this screen about you when it is about
              the thing you are giving away. */}
          {tab === 'offerings' && (
          <div className="grid gap-10 md:grid-cols-2 md:gap-x-8 max-w-5xl mx-auto md:h-full md:min-h-0">

            {/* Leave him something. No heading: the header above says what
                the drawer is, and the card's own first line does the rest. */}
            <div className="order-1 md:order-1">
              {signedIn && (
                <Card>
                  {/* What this actually is, before the box you type in.
                      Without it the form is a message box on a website; with
                      it, it is a thing you are leaving somewhere for someone
                      to find, which is the whole mechanic. */}
                  <p style={{ color: kawaii.ink, fontSize: 13.5, fontWeight: 600, lineHeight: 1.55, marginBottom: 13 }}>
                    Leave {BIRD_NAME} a message or a picture. He'll find it in
                    his world and decide if he likes it. The more he likes,
                    the more influence he gives you in his world.
                    <span style={{ color: kawaii.inkSoft }}>
                      {' '}{notesLine(role?.notes, submitsPerDay(rank?.name))}
                    </span>
                  </p>
                  <Composer onSubmitted={onSubmitted} />
                </Card>
              )}
              {!signedIn && (
                <Card
                  className="text-center leading-relaxed"
                  style={{ color: kawaii.inkSoft, fontSize: 14.5, fontWeight: 600, padding: '22px 18px' }}
                >
                  Leave {BIRD_NAME} a message or a picture. He'll find it in
                  his world and decide if he likes it. The more he likes, the
                  more influence he gives you in his world.
                  <div style={{ marginTop: 14 }}>
                    <KawaiiCta intent="primary" small onClick={connect}>connect to {BIRD_NAME}</KawaiiCta>
                  </div>
                </Card>
              )}
            </div>

            {/* What he has been left. A column again, but inside a fixed
                viewport this time: the first column grew with the list and
                decided how tall the drawer was, the carousel that replaced
                it showed one card and hid the rest. This scrolls, the
                drawer does not move, and the images load as they come into
                view. */}
            <div className="order-2 md:order-2 min-w-0">
              <FeedList
                items={visible}
                onMore={onMore}
                standing={accepted}
                counts={counts}
                empty={<p className="text-[13.5px] font-semibold text-[#2f2650]/60 italic py-2">he hasn't kept anything yet.</p>}
              />
            </div>

          </div>
          )}
        </div>
      </div>
    </div>
  )
}

// The noticeboard. Newest first, every card, in a viewport the same
// height the carousel used (so the drawer's shape did not change), with
// a line at the bottom saying how many are on it and how many are still
// waiting on him, which is the number a new visitor is really asking.
// (the board is what he has kept, newest read first, and counts says how many wait and how many he read today: a board
// of the newest that came was all "waiting" once a thousand a day came, 1 Nov)
function FeedList({ items, onMore, empty, standing = 0, counts = null }) {
  if (!items.length) return empty
  const waiting = counts ? counts.waiting : items.filter((s) => !s.status || s.status === 'pending' || s.status === 'found').length
  return (
    <div className="relative min-w-0">
      <div
        className="kawaii-scroll min-w-0 overflow-x-hidden flex flex-col gap-3"
        style={{ height: 'clamp(240px, 44vh, 440px)', overflowY: 'auto', paddingBottom: 12, paddingRight: 4 }}
      >
        {items.map((s) => <FeedItem key={s.id} s={s} standing={standing} />)}
        {onMore && (
          <button
            type="button"
            onClick={onMore}
            className="self-center text-[12px] font-bold rounded-full px-4 py-1.5"
            style={{ color: kawaii.ink, background: kawaii.creamDeep, border: kawaii.outlineThin, cursor: 'pointer' }}
          >
            older ones
          </button>
        )}
      </div>
      <div className="flex items-center justify-between" style={{ marginTop: 8 }}>
        <span style={{ color: kawaii.inkSoft, fontSize: 12, fontWeight: 700 }}>
          {counts ? `the ${items.length} he read most recently` : `the ${items.length} most recent`}
        </span>
        {waiting > 0 && (
          <span style={{ color: kawaii.inkSoft, fontSize: 12, fontWeight: 700 }}>
            {counts ? `${counts.readToday} read today, ${waiting} waiting` : `${waiting} still waiting on him`}
          </span>
        )}
      </div>
    </div>
  )
}

// A message or a picture, and nothing else: money was never an offering
// he could read (Sam, 3 Oct, the tribute path came out whole).
const DOCK_TYPES = [['text', 'a message'], ['image', 'a picture']]

function Composer({ onSubmitted }) {
  const [type, setType] = useState('text')
  const [text, setText] = useState('')       // body / image caption
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)
  const pushToast = useGameStore((st) => st.pushToast)
  const setMsg = (m) => { if (m?.text) pushToast(m.text, m.ok !== false) }

  const pick = (t) => { setType(t); setMsg(null) }
  const onFile = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (!f.type.startsWith('image/')) { setMsg({ ok: false, text: 'images only.' }); return }
    if (f.size > 10 * 1024 * 1024) { setMsg({ ok: false, text: 'max 10mb.' }); return }
    if (preview) URL.revokeObjectURL(preview)
    setFile(f); setPreview(URL.createObjectURL(f)); setMsg(null)
  }
  const canSubmit = !busy && (
    (type === 'text' && text.trim()) ||
    (type === 'image' && file)
  )

  const submit = useCallback(async (e) => {
    e.preventDefault()
    if (busy) return
    if (type === 'text' && !text.trim()) return
    if (type === 'image' && !file) return
    setBusy(true); setMsg(null)
    try {
      let res
      if (type === 'image') {
        const fd = new FormData(); fd.append('file', file)
        const up = await authFetch('/api/sacrifice/upload', { method: 'POST', body: fd })
        const ub = await up.json().catch(() => ({}))
        if (!up.ok) { setMsg({ ok: false, text: ub.error || `upload failed (${up.status})` }); return }
        res = await authFetch('/api/sacrifice', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'image', imageUrl: ub.imageUrl, text }) })
      } else {
        res = await authFetch('/api/sacrifice', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'text', text }) })
      }
      const body = await res.json().catch(() => ({}))
      if (res.ok) {
        if (preview) URL.revokeObjectURL(preview)
        setText(''); setFile(null); setPreview(null)
        // The server's message is not decoration: it says whether the world
        // actually took it. A failed handoff returns "recorded, the city is
        // crowded today", and hardcoding a cheerful line over the top is how
        // an offering that never reached the world still looked like it had.
        setMsg({ ok: true, text: (body.message || "he\u2019ll come across it.").toLowerCase() })
        onSubmitted?.()
      } else if (res.status === 429) {
        // the shrine's own refusal says when the next one opens, and the clock time is said here, where they are. a
        // refusal without its words came from somewhere else, and is not the allowance (30 Oct)
        const at = Number(body.opensAt)
        const clock = Number.isFinite(at) && at > Date.now() ? new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null
        setMsg({ ok: false, text: body.message ? (clock ? `${body.message.replace(/\.$/, '')}, at ${clock}.` : body.message) : 'too many tries just now. give it a minute and try again.' })
      } else if (res.status === 401) {
        setMsg({ ok: false, text: 'session expired. sign in again and retry.' })
      } else if (res.status === 409) {
        setMsg({ ok: false, text: body.error || 'that one’s already been offered.' })
      } else {
        setMsg({ ok: false, text: body.error || `failed (${res.status})` })
      }
    } catch (err) {
      setMsg({ ok: false, text: `network error: ${err.message}` })
    } finally {
      setBusy(false)
    }
  }, [type, text, file, preview, busy, onSubmitted])

  return (
    <form onSubmit={submit} className="space-y-2">
      {/* type */}
      <div className="flex gap-2">
        {DOCK_TYPES.map(([t, label]) => (
          <button
            key={t}
            type="button"
            onClick={() => pick(t)}
            className={`px-5 py-2 text-[14px] rounded-full transition-colors ${
              type === t ? 'bg-[#f0b064] text-[#2f2650] font-extrabold' : 'text-[#2f2650]/55 hover:text-[#2f2650] bg-[#2f2650]/5'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {type === 'text' && (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, TEXT_LIMIT))}
          placeholder="a thought, a question, a confession, anything. he'll read it."
          rows={6}
          disabled={busy}
          className="w-full rounded-xl px-3.5 py-3 text-[15px] font-medium text-[#2f2650] placeholder-[#2f2650]/45 resize-none focus:outline-none leading-relaxed"
          style={{ background: kawaii.creamDeep, border: kawaii.outlineThin }}
        />
      )}

      {type === 'image' && (
        <div className="space-y-2">
          <label className="block px-3 py-4 text-center cursor-pointer rounded-xl" style={{ background: kawaii.creamDeep, border: kawaii.stitch }}>
            <input type="file" accept="image/*" onChange={onFile} disabled={busy} className="hidden" />
            <span style={{ color: kawaii.inkSoft, fontSize: 13.5, fontWeight: 700 }}>{file ? file.name : 'choose an image, he actually sees it'}</span>
          </label>
          {preview && <img src={preview} alt="" className="max-h-32 w-auto border border-[#2f2650]/25 mx-auto" />}
          <input
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, TEXT_LIMIT))}
            placeholder="a word about it (optional)"
            disabled={busy}
            className="w-full rounded-xl px-3 py-2 text-[14px] font-medium text-[#2f2650] placeholder-[#2f2650]/45 focus:outline-none"
            style={{ background: kawaii.creamDeep, border: kawaii.outlineThin }}
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-2 min-h-[20px]">
        <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>
          {null}
        </span>
        <KawaiiCta type="submit" disabled={!canSubmit} small className="shrink-0">
          {busy ? 'leaving it…' : 'leave it for him'}
        </KawaiiCta>
      </div>
    </form>
  )
}

// Exported because the profile drawer shows the same card for your own
// offerings, and a second copy of it would be a second thing to keep in
// step. On your own profile the handle is you, so it can be dropped.
export function FeedItem({ s, showHandle = true, standing = 0, mine = null }) {
  const phase = s.status || 'pending'
  const kept = phase === 'accepted'
  const own = mine ?? !!s.mine
  // Still his, just not on the wall: the wall holds so many and the
  // oldest makes room for the newest. Said on the card, because a giver
  // who goes looking for their piece in the ruins and finds it gone
  // deserves the reason.
  const offWall = kept && !!s.wallLeftAt
  // Released is its own colour of gone: he kept it, lived with it a while
  // up in the ruins, and then his tastes moved. The card says that, since
  // "he let it go" would erase the part where it mattered.
  const released = phase === 'released'
  const gone = phase === 'rejected' || released
  // Never read: it waited longer than he had (30 Oct, Sam: "ones that get missed can expire - we tell the original
  // poster that it's expired"). Not a verdict on it, so not the let go colour.
  const expired = phase === 'expired'
  // Read for him by Glint, the friend he made on the cafe computer, in the hour before it would have expired, and
  // answered in Glint's words (31 Oct): his, not Pino's, and said so
  const glint = phase === 'glint'
  const seen = phase === 'found'
  // Your own that he has not read yet says where it is in the line: he
  // reads them oldest first, a few at a time at the shrine, and on a busy
  // day a plain "waiting" looked like nothing was happening (28 Oct).
  // (and with a sorter of his, where it put it: he wrote one at the cafe computer for the notes, 31 Oct. lib/ui/sorterWords.js)
  const waitingWords = sortedWaiting(s) || (typeof s.ahead !== 'number' ? 'waiting' : s.ahead > 0 ? `waiting, ${s.ahead} ahead of it` : 'waiting, next to be read')
  // A real card with the ink outline and the offset shadow, like the rest
  // of the kit. The old row was a tinted box with a coloured left edge,
  // which is a pattern from a different product and read as a log line.
  return (
    <div
      style={{
        background: kawaii.cream,
        border: kawaii.outlineThin,
        borderRadius: 16,
        boxShadow: kawaii.dropSm,
        padding: '11px 13px',
      }}
    >
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <span style={{ color: kawaii.ink, fontSize: 13.5, fontWeight: 800 }} className="truncate">
          {showHandle ? (s.handle || 'anon') : ''}
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <span
            style={{
              background: kept ? '#a8d6a0' : gone ? '#eda6a0' : glint ? '#b9e3ef' : expired ? '#d9d3e6' : kawaii.creamDeep,
              color: kawaii.ink,
              border: kawaii.outlineThin,
              borderRadius: 999,
              boxShadow: kawaii.dropSm,
              padding: '2px 11px',
              fontSize: 12,
              fontWeight: 800,
              whiteSpace: 'nowrap',
            }}
          >
            {kept ? (offWall ? 'he kept it, it came down to make room' : 'he kept it') : released ? 'he kept it a while, then took it down' : gone ? 'he let it go' : glint ? 'Glint read it for him' : expired ? expiredWords(s) : seen ? 'he noticed it' : waitingWords}
          </span>
          <span style={{ color: kawaii.inkSoft, fontSize: 12, fontWeight: 700 }}>
            {s.judgedAtWall || s.submittedAtWall ? agoShort(s.judgedAtWall || s.submittedAtWall) : 'now'}
          </span>
        </span>
      </div>

      {s.type === 'financial' && (s.asset || s.amount != null) && (
        <div className="mb-1.5">
          <span
            style={{
              background: '#a8d6a0', color: kawaii.ink, border: kawaii.outlineThin,
              borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 800,
            }}
          >
            {s.amount != null ? `${s.amount} ` : ''}{s.asset}{s.usdValue != null ? ` · $${Math.round(s.usdValue)}` : ''}
          </span>
        </div>
      )}

      {/* Picture beside the words. It used to sit above them in a 200px
          contain frame, which kept every card the same height (the reason
          it was built that way: height:auto made the carousel bounce) but
          spent the whole width on a portrait photo with a band of cream
          either side, and pushed the message out of view.
          Cropped rather than contained, because these arrive portrait,
          landscape and square, and one consistent shape reads as a feed
          while three different ones read as a bug. */}
      <div className="flex" style={{ gap: 12, marginBottom: s.text || s.reaction ? 8 : 0 }}>
        {s.imageUrl && (
          <img
            src={s.imageUrl}
            alt="offering"
            loading="lazy"
            style={{
              width: 92, height: 138, flex: 'none',
              objectFit: 'cover',
              display: 'block',
              background: kawaii.creamDeep,
              border: kawaii.outlineThin,
              borderRadius: 10,
              alignSelf: 'flex-start',
            }}
          />
        )}
        {s.text && (
          <p style={{ color: kawaii.ink, fontSize: 14, fontWeight: 600, lineHeight: 1.5, minWidth: 0 }} className="line-clamp-6">
            {s.text}
          </p>
        )}
      </div>

      {s.reaction && (
        <p style={{ marginTop: 7, color: kept ? '#8a5a12' : glint ? '#175f83' : '#a83832', fontSize: 13.5, fontStyle: 'italic', lineHeight: 1.5 }}>
          {glint ? `Glint: ${s.reaction}` : s.reaction}
        </p>
      )}

      {/* The question he put to the giver. Everyone sees that he asked;
          only the giver gets the box, and only while it stands. */}
      {kept && s.asked && (
        <Asked s={s} own={own} />
      )}

      {kept && (
        <Talk s={s} standing={standing} own={own} share={<XShareButton href={shareIntent(s, kept)} />} />
      )}
      {gone && (
        <div style={{ marginTop: 9 }}>
          <XShareButton href={shareIntent(s, kept)} />
        </div>
      )}
    </div>
  )
}

// What he asked the giver, and their one answer to it.
//
// Measured 15 Sep: in 199 kept replies he put a real question to the
// giver once, and it had nowhere to go. Now the judge asks as a field of
// its own, the question rides the verdict here, and this is the box.
// Owner only, once; the answer lands in the thread below marked as an
// answer, which is how he tells it from small talk when he reads it on
// his next visit to the ruins. No rank needed: he asked.
function Asked({ s, own }) {
  const { signedIn } = useAccount()
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState(null)
  const [done, setDone] = useState(false)
  const answered = done || !!s.answeredAt
  const send = async () => {
    const body = text.trim()
    if (!body || busy) return
    setBusy(true); setNote(null)
    try {
      const r = await authFetch(`/api/sacrifice/${s.id}/answer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: body }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) { setNote(j.error || 'that did not land.'); return }
      setDone(true)
    } catch {
      setNote('that did not land.')
    } finally {
      setBusy(false)
    }
  }
  return (
    <div style={{ marginTop: 8 }}>
      <p style={{ color: '#8a5a12', fontSize: 13.5, fontWeight: 700, lineHeight: 1.5 }}>
        <span style={{ color: kawaii.inkSoft, fontWeight: 800, fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase', marginRight: 6 }}>
          {own ? 'he asked you' : `he asked ${s.handle || 'them'}`}
        </span>
        {s.asked}
      </p>
      {own && signedIn && !answered && (
        <div style={{ marginTop: 6 }}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 280))}
            placeholder="answer him"
            rows={2}
            style={{
              width: '100%', resize: 'none', background: kawaii.creamDeep, color: kawaii.ink,
              border: kawaii.outlineThin, borderRadius: 10, padding: '7px 9px', fontSize: 13.5, fontFamily: ui.font, lineHeight: 1.45,
            }}
          />
          <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 6 }}>
            <KawaiiCta small intent="primary" onClick={send} disabled={busy || !text.trim()}>
              {busy ? 'sending' : 'send him your answer'}
            </KawaiiCta>
            <span style={{ color: kawaii.inkSoft, fontSize: 11.5, fontWeight: 600 }}>once. he reads it next time he is in the ruins.</span>
          </div>
          {note && <p style={{ color: '#a83832', fontSize: 12, fontWeight: 700, marginTop: 4 }}>{note}</p>}
        </div>
      )}
      {own && answered && (
        <p style={{ color: kawaii.inkSoft, fontSize: 12, fontWeight: 700, marginTop: 4 }}>
          you answered. he reads it next time he is in the ruins.
        </p>
      )}
    </div>
  )
}

/** A drawn heart and a drawn bubble, because no emoji anywhere in this product. */
function HeartMark({ filled }) {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true" style={{ display: 'block' }}>
      <path
        d="M8 13.2 3.4 8.8a2.9 2.9 0 0 1 0-4.1 2.8 2.8 0 0 1 4 0l.6.6.6-.6a2.8 2.8 0 0 1 4 0 2.9 2.9 0 0 1 0 4.1Z"
        fill={filled ? '#c2586f' : 'none'} stroke="#c2586f" strokeWidth="1.5" strokeLinejoin="round"
      />
    </svg>
  )
}
function BubbleMark() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true" style={{ display: 'block' }}>
      <path
        d="M3 3.5h10a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-1.5 1.5H7.2L4.5 14v-2.5H3A1.5 1.5 0 0 1 1.5 10V5A1.5 1.5 0 0 1 3 3.5Z"
        fill="none" stroke="#5f42ab" strokeWidth="1.5" strokeLinejoin="round"
      />
    </svg>
  )
}

// The talk under an offering: a heart, a thread, and a line to add to it.
//
// The doors are lib/community.js's: a heart for anyone connected, a line
// for the owner always and for anyone once he has spoken under it, else
// for somebody he knows. A thread he has closed takes no more lines. The
// lock says why in words rather than greying out, because a greyed heart
// on a thing you love reads as broken. He reads the thread when he next
// reads the board, wall or no wall, and the people in it get named in
// his diary, which the composer says so nobody is surprised. Each line
// has its own heart, his included: that is how he learns which of his
// words landed.
function Talk({ s, standing = 0, own = false, share = null, openByDefault = false }) {
  const { signedIn } = useAccount()
  // The feed's own counts are the truth, refreshed by the poll. A press
  // overrides them only while the server still reports the count it had
  // when you pressed: once the poll comes back with your press in it,
  // the server takes over again. Adding a local increment on top of a
  // live count double-counted ("2 said something" after one line, "1"
  // after a refresh).
  const [pressed, setPressed] = useState(null)   // { at: server likes then, likes, liked }
  const [said, setSaid] = useState(null)         // { at: server comments then, count }
  const [open, setOpen] = useState(!!openByDefault)
  const [thread, setThread] = useState(null)
  const [meta, setMeta] = useState(null)         // { closedAt, wallLeftAt, pinoLines } from the thread read
  const [text, setText] = useState('')
  const [note, setNote] = useState(null)
  const [busy, setBusy] = useState(false)
  const serverLikes = s.likes || 0
  const serverComments = s.comments || 0
  const likeCount = pressed && pressed.at === serverLikes ? pressed.likes : serverLikes
  const isLiked = pressed && pressed.at === serverLikes ? pressed.liked : !!s.liked
  const commentCount = said && said.at === serverComments ? said.count : serverComments
  // What the thread read says wins over what the card came with: the
  // card is a poll old, the thread is what you are looking at.
  const closed = !!(meta ? meta.closedAt : s.talkClosedAt)
  const pinoSpoke = (meta ? meta.pinoLines : s.pinoLines || 0) > 0
  const likeLock = own ? 'your own' : talkLockReason({ signedIn, what: 'like' })
  const commentLock = talkLockReason({ signedIn, kept: standing, owner: own, pinoSpoke, closed })

  const press = async () => {
    if (likeLock) { setNote(own ? 'not your own' : likeLock); return }
    try {
      const res = await authFetch(`/api/sacrifice/${s.id}/like`, { method: 'POST' })
      const d = await res.json().catch(() => ({}))
      if (res.status === 401) { setNote('sign in again'); return }
      if (!res.ok) { setNote(d.error || 'did not land'); return }
      setPressed({ at: serverLikes, likes: d.likes || 0, liked: !!d.liked }); setNote(null)
    } catch { setNote('did not land') }
  }

  const readThread = useCallback(async () => {
    try {
      const r = await (signedIn ? authFetch : fetch)(`/api/sacrifice/${s.id}/comments`, { cache: 'no-store' })
      const d = await r.json().catch(() => ({}))
      setThread(Array.isArray(d.comments) ? d.comments : [])
      setMeta({ closedAt: d.closedAt || null, wallLeftAt: d.wallLeftAt || null, pinoLines: d.pinoLines || 0 })
    } catch { setThread([]) }
  }, [s.id, signedIn])

  // Unfolded from the start (the talk tab, a thread landed on): read it
  // straight away rather than waiting for a press that never comes.
  useEffect(() => {
    if (open && thread === null) readThread()
  }, [open, thread, readThread])

  const toggleThread = () => setOpen((v) => !v)

  const say = async (e) => {
    e.preventDefault()
    if (busy || !text.trim()) return
    if (commentLock) { setNote(commentLock); return }
    setBusy(true)
    try {
      const res = await authFetch(`/api/sacrifice/${s.id}/comments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }),
      })
      const d = await res.json().catch(() => ({}))
      if (res.status === 401) { setNote('sign in again'); return }
      if (!res.ok) { setNote(d.error || 'did not land'); return }
      const next = [...(thread || []), d.comment]
      setThread(next)
      // The thread is loaded whenever you can post, so its length is the
      // true count, not a guess on top of a stale one.
      setSaid({ at: serverComments, count: next.length })
      setText('')
      setNote(d.left === 0 ? 'that was your last one for today' : null)
    } catch { setNote('did not land') } finally { setBusy(false) }
  }

  // A heart on one line. The server says no to your own; everyone else's
  // is a press, his included.
  const heartLine = async (c) => {
    if (!signedIn) { setNote('connect to him'); return }
    try {
      const res = await authFetch(`/api/sacrifice/comment/${c.id}/like`, { method: 'POST' })
      const d = await res.json().catch(() => ({}))
      if (!res.ok) { setNote(d.error || 'did not land'); return }
      setThread((cur) => (cur || []).map((x) => (x.id === c.id ? { ...x, likes: d.likes || 0, liked: !!d.liked } : x)))
      setNote(null)
    } catch { setNote('did not land') }
  }

  // The same button as the share control beside it, measure for measure:
  // padding, type size, outline and the solid offset shadow. Three pills
  // in a row read as one kit only if they are one kit.
  const pill = {
    display: 'inline-flex', alignItems: 'center', gap: 7,
    background: kawaii.cream, border: kawaii.outlineThin, borderRadius: 999,
    boxShadow: kawaii.dropSm,
    padding: '5px 14px', fontSize: 12.5, fontWeight: 800, color: kawaii.ink, cursor: 'pointer',
    fontFamily: ui.font, lineHeight: 'normal',
  }
  const lastHis = closed && thread ? [...thread].reverse().find((c) => c.pino) : null

  return (
    <div className="min-w-0" style={{ marginTop: 9 }}>
      <div className="flex items-center gap-2 flex-wrap">
        {share}
        <button type="button" onClick={press} style={{ ...pill, opacity: likeLock ? 0.75 : 1 }} aria-pressed={isLiked}
          title={likeLock || (isLiked ? 'you liked this' : 'like this')}>
          <HeartMark filled={isLiked} />{likeCount > 0 ? likeCount : 'like'}
        </button>
        <button type="button" onClick={toggleThread} style={pill} aria-expanded={open}>
          <BubbleMark />{commentCount > 0 ? `${commentCount} said something` : 'say something'}
        </button>
        {closed && (
          <span style={{ color: kawaii.inkSoft, fontSize: 11.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase' }}>closed by him</span>
        )}
        {note && <span style={{ color: kawaii.inkSoft, fontSize: 11.5, fontWeight: 700 }}>{note}</span>}
      </div>

      {open && (
        <div style={{ marginTop: 8, borderTop: kawaii.stitch, paddingTop: 8 }}>
          {thread === null && <p style={{ color: kawaii.inkSoft, fontSize: 12, fontWeight: 600 }}>reading...</p>}
          {thread && thread.length === 0 && (
            <p style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 600 }}>nobody has said anything under this yet.</p>
          )}
          {thread && thread.map((c) => (
            <div key={c.id} className="flex items-start gap-2" style={{ marginBottom: 5 }}>
              <p className="min-w-0 flex-1" style={{ color: kawaii.ink, fontSize: 13, lineHeight: 1.5 }}>
                <span style={{ fontWeight: 800, color: c.pino ? '#8a5a12' : kawaii.ink }}>{c.pino ? BIRD_NAME : c.handle}</span>
                {c.answer && (
                  <span style={{ marginLeft: 6, color: kawaii.inkSoft, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase' }}>answered him</span>
                )}
                {lastHis && c.id === lastHis.id && (
                  <span style={{ marginLeft: 6, color: kawaii.inkSoft, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase' }}>his last word</span>
                )}
                <span style={{ color: kawaii.inkSoft, fontSize: 11, marginLeft: 6 }}>{agoShort(c.at || Date.now())}</span>
                <br />
                {/* His answers wear the same italic amber as his verdicts, so
                    a reader knows at a glance which line is the bird's. */}
                <span style={{ fontWeight: 600, fontStyle: c.pino ? 'italic' : 'normal', color: c.pino ? '#8a5a12' : kawaii.ink }}>{c.pino ? withHisLinks(c.body) : c.body}</span>
              </p>
              <button
                type="button"
                onClick={() => heartLine(c)}
                aria-pressed={!!c.liked}
                title={c.liked ? 'you hearted this line' : 'heart this line'}
                className="shrink-0"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: 'none', padding: '2px 2px', cursor: 'pointer', color: kawaii.inkSoft, fontSize: 11, fontWeight: 800, fontFamily: ui.font, marginTop: 2 }}
              >
                <HeartMark filled={!!c.liked} />{c.likes > 0 ? c.likes : ''}
              </button>
            </div>
          ))}
          {closed ? (
            <p style={{ color: kawaii.inkSoft, fontSize: 12, fontWeight: 700, marginTop: 6 }}>he closed this one. his last word is above.</p>
          ) : commentLock ? (
            <p style={{ color: kawaii.inkSoft, fontSize: 12, fontWeight: 700, marginTop: 6 }}>{commentLock}.</p>
          ) : (
            <form onSubmit={say} className="flex items-center gap-2" style={{ marginTop: 8 }}>
              <input
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 240))}
                placeholder={own ? 'answer them, he reads this too' : pinoSpoke ? 'join in. he reads these, and names who said them' : 'a line. he reads these, and names who said them'}
                disabled={busy}
                aria-label="say something under this offering"
                className="flex-1 min-w-0 rounded-xl px-3 py-2 text-[13px] font-medium text-[#2f2650] placeholder-[#2f2650]/45 focus:outline-none"
                style={{ background: kawaii.creamDeep, border: kawaii.outlineThin }}
              />
              <KawaiiCta type="submit" small disabled={busy || !text.trim()}>say it</KawaiiCta>
            </form>
          )}
        </div>
      )}
    </div>
  )
}

// A link in one of his lines goes somewhere: his own site, where a
// program he wrote is up. Only his lines, only his site (the cleaner
// refuses anything else at the door), rendered as a link so a reader
// can go and see the thing.
function withHisLinks(body) {
  const text = String(body || '')
  const parts = text.split(/(https:\/\/[a-z0-9-]+\.github\.io\/\S+)/i)
  if (parts.length === 1) return text
  return parts.map((part, i) => (
    /^https:\/\//i.test(part)
      ? <a key={i} href={part.replace(/[.,;:!?)]+$/, '')} target="_blank" rel="noopener noreferrer" style={{ color: '#8a5a12', textDecoration: 'underline' }}>{part}</a>
      : part
  ))
}

// The talk tab: every kept piece somebody has said something under, the
// one with the newest words first. This is where the offerings become
// conversations: the piece, what he said about it, the last line as a
// teaser, and the thread unfolded on a press. A thread landed on from
// its own page (/talk/<id>) goes first and opens on its own.
function TalkList({ standing = 0, landOn = null }) {
  const { signedIn } = useAccount()
  const [rows, setRows] = useState(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let dead = false
    const pull = async () => {
      try {
        const r = await (signedIn ? authFetch : fetch)('/api/sacrifices?talk=1&limit=60', { cache: 'no-store' })
        const d = await r.json().catch(() => ({}))
        if (dead) return
        if (Array.isArray(d.threads)) { setRows(d.threads); setFailed(false) } else setFailed(true)
      } catch { if (!dead) setFailed(true) }
    }
    pull()
    const id = setInterval(pull, POLL_MS)
    return () => { dead = true; clearInterval(id) }
  }, [signedIn])
  const list = useMemo(() => {
    if (!rows || !landOn) return rows
    const i = rows.findIndex((t) => t.id === landOn)
    if (i <= 0) return rows
    return [rows[i], ...rows.slice(0, i), ...rows.slice(i + 1)]
  }, [rows, landOn])
  return (
    <div className="min-w-0">
      <p style={{ color: kawaii.inkSoft, fontSize: 13, fontWeight: 600, lineHeight: 1.55, marginBottom: 12 }}>
        Every kept piece somebody has said something under, newest words first. He answers when he
        reads the board, from the ruins or the cafe. Once he has spoken under one, anyone connected
        can join, until he closes it.
      </p>
      {list === null && !failed && <p style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 600 }}>reading the board...</p>}
      {list === null && failed && <p style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 600 }}>could not read the board just now.</p>}
      {list && list.length === 0 && (
        <p className="text-[13.5px] font-semibold text-[#2f2650]/60 italic py-2">nobody has said anything under anything yet. be the first.</p>
      )}
      {list && list.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {list.map((t) => <ThreadCard key={t.id} t={t} standing={standing} open={t.id === landOn} />)}
        </div>
      )}
    </div>
  )
}

// One thread as a card: the piece in brief, his verdict, the state of
// the talk (he answered, closed, how many voices), the last line, and
// the thread itself underneath.
function ThreadCard({ t, standing = 0, open = false }) {
  const offWall = !!t.wallLeftAt
  const closed = !!t.talkClosedAt
  const tag = {
    border: kawaii.outlineThin, borderRadius: 999, boxShadow: kawaii.dropSm,
    padding: '2px 10px', fontSize: 11.5, fontWeight: 800, whiteSpace: 'nowrap', color: kawaii.ink,
  }
  return (
    <div style={{ background: kawaii.cream, border: kawaii.outlineThin, borderRadius: 16, boxShadow: kawaii.dropSm, padding: '11px 13px' }}>
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <span style={{ color: kawaii.ink, fontSize: 13.5, fontWeight: 800 }} className="truncate">{t.handle || 'anon'}</span>
        <span className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
          {t.pinoLines > 0 && <span style={{ ...tag, background: '#f3d59b' }}>{BIRD_NAME} answered</span>}
          <span style={{ ...tag, background: closed ? kawaii.creamDeep : '#a8d6a0' }}>{closed ? 'closed' : 'open'}</span>
          {t.voices > 0 && <span style={{ ...tag, background: kawaii.creamDeep }}>{t.voices} {t.voices === 1 ? 'voice' : 'voices'}</span>}
          <span style={{ color: kawaii.inkSoft, fontSize: 12, fontWeight: 700 }}>{t.lastAt ? agoShort(t.lastAt) : ''}</span>
        </span>
      </div>
      <div className="flex" style={{ gap: 12, marginBottom: 6 }}>
        {t.imageUrl && (
          <img
            src={t.imageUrl}
            alt="offering"
            loading="lazy"
            style={{ width: 56, height: 84, flex: 'none', objectFit: 'cover', display: 'block', background: kawaii.creamDeep, border: kawaii.outlineThin, borderRadius: 8, alignSelf: 'flex-start' }}
          />
        )}
        <div className="min-w-0">
          {t.text && (
            <p style={{ color: kawaii.ink, fontSize: 13.5, fontWeight: 600, lineHeight: 1.5 }} className="line-clamp-3">{t.text}</p>
          )}
          {t.reaction && (
            <p style={{ marginTop: 4, color: '#8a5a12', fontSize: 13, fontStyle: 'italic', lineHeight: 1.5 }} className="line-clamp-2">{t.reaction}</p>
          )}
          {offWall && (
            <p style={{ marginTop: 4, color: kawaii.inkSoft, fontSize: 11.5, fontWeight: 700 }}>came down from the wall to make room, still kept</p>
          )}
        </div>
      </div>
      {t.lastLine && (
        <p style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 600, lineHeight: 1.45 }} className="line-clamp-2">
          <span style={{ fontWeight: 800, color: t.lastByHim ? '#8a5a12' : kawaii.ink }}>{t.lastByHim ? BIRD_NAME : t.lastBy}</span>
          {': '}
          <span style={{ fontStyle: t.lastByHim ? 'italic' : 'normal' }}>{t.lastLine}</span>
        </p>
      )}
      <Talk s={t} standing={standing} own={!!t.mine} openByDefault={open} share={<XShareButton href={shareThreadIntent(t)} />} />
    </div>
  )
}

// What gets posted when someone shares his verdict on their offering.
//
// It used to copy a link to the clipboard and say nothing, so it read as a
// dead button: no confirmation, nothing on screen, nothing in the paste
export default OfferingsDock
